import React, { useEffect, useMemo, useState } from "react";
import { toReportNumber } from './reportNumbers';
import Chart from "./Chart";
import ActionButton from '../../ActionButton';
import KPI from "./KPI";
import Table from "./Table";
import { buildOverviewReportRows } from './overviewReportRows';
import OrdersHeatmap from "./Heatmap";

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec",];
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const OVERVIEW_TOP_ITEMS_LIMIT = 5; // amount of items to rank in the items chart

const CARD_CLASS =
  "reports-panel reports-heatmap bg-white border border-gray-200 rounded-lg p-4 shadow-sm dark:bg-gray-900 dark:border-gray-700";
const TITLE_CLASS =
  "reports-panel-title text-sm text-blue-700 dark:text-blue-300 font-medium mb-2";
const EMPTY_CLASS = "text-sm text-gray-500 dark:text-gray-400";

// Format date and time values without shifting reporting dates across time zones.
const formatMonthDay = (isoDate) => {
  if (!isoDate) return "";

  const [, month, day] = String(isoDate)
    .slice(0, 10)
    .split("-")
    .map(Number);

  if (
    !Number.isFinite(month) ||
    !Number.isFinite(day) ||
    month < 1 ||
    month > 12
  ) {
    return String(isoDate);
  }

  return `${MONTHS[month - 1]} ${day}`;
};

const formatHour12 = (hour) => {
  const h = Number(hour);

  if (!Number.isFinite(h) || h < 0 || h > 23) {
    return "";
  }

  const suffix = h < 12 ? "AM" : "PM";
  const hour12 = h % 12 === 0 ? 12 : h % 12;

  return `${hour12}:00 ${suffix}`;
};

const toWeekStartYmd = (isoDate) => {
  if (!isoDate) return null;

  const date = new Date(`${String(isoDate).slice(0, 10)}T00:00:00Z`);

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  const weekdayMon0 = (date.getUTCDay() + 6) % 7;

  date.setUTCDate(date.getUTCDate() - weekdayMon0);

  return date.toISOString().slice(0, 10);
};

const weekdayFromIso = (isoDate) => {
  if (!isoDate) return null;

  const date = new Date(`${String(isoDate).slice(0, 10)}T00:00:00Z`);

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  const weekdayMon0 = (date.getUTCDay() + 6) % 7;

  return WEEKDAYS[weekdayMon0];
};

const monthKeyFromIso = (isoDate) =>
  String(isoDate || "").slice(0, 7);

const formatMonth = (monthKey) => {
  const [year, month] = String(monthKey || "").split("-").map(Number);

  if (!Number.isFinite(year) || !Number.isFinite(month)) {
    return String(monthKey || "");
  }

  return `${MONTHS[month - 1]} ${year}`;
};

const formatWeek = (weekStart) => {
  const start = new Date(`${weekStart}T00:00:00Z`);

  if (Number.isNaN(start.getTime())) return String(weekStart || "");

  const end = new Date(start);
  end.setUTCDate(end.getUTCDate() + 6);

  return `${formatMonthDay(weekStart)} - ${formatMonthDay(
    end.toISOString().slice(0, 10)
  )}`;
};

const addDaysYmd = (isoDate, days) => {
  const date = new Date(`${String(isoDate).slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return "";
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
};

const monthEndYmd = (monthKey) => {
  const [year, month] = String(monthKey).split("-").map(Number);
  if (!Number.isFinite(year) || !Number.isFinite(month)) return "";
  return new Date(Date.UTC(year, month, 0)).toISOString().slice(0, 10);
};

// Render the Overview tab from prefiltered report datasets supplied by the parent.
const ChartsSection = ({
  filters = {},
  ordersByDay = [],
  avgBumpTimeByDay = [],
  ordersHeatmap = [],
  itemsData = [],
  itemsByDay = [],
  fetchItemsSummaryForRange,
  ordersList = [],
  ordersBumpMinutes = [],
  isDark = true,
}) => {
  // Track the Total Orders chart's month, week, and day drill state.
  const [DrillLevel, setDrillLevel] = useState("month");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedWeek, setSelectedWeek] = useState("");

  // Track the Top Items chart's month, week, and day drill state.
  const [itemsDrillLevel, setItemsDrillLevel] = useState("month");
  const [itemsSelectedMonth, setItemsSelectedMonth] = useState("");
  const [itemsSelectedWeek, setItemsSelectedWeek] = useState("");

  // Store period summaries loaded from the existing item-summary route when daily item data is unavailable.
  const [legacyItemsByPeriod, setLegacyItemsByPeriod] = useState([]);

  // Combine daily order totals with daily average bump times for the overview chart.
  const overlayData = useMemo(() => {
    const map = new Map();

    for (const row of ordersByDay) {
      const date = String(row?.date || "").slice(0, 10);

      if (!date) continue;

      map.set(date, {
        ...(map.get(date) || {}),
        date,
        total_orders: Number(row?.total_orders || 0),
      });
    }

    for (const row of avgBumpTimeByDay) {
      const date = String(row?.date || "").slice(0, 10);

      if (!date) continue;

      map.set(date, {
        ...(map.get(date) || {}),
        date,
        avg_bump_time_minutes: Number(
          row?.avg_bump_time_minutes || 0,
        ),
      });
    }

    return Array.from(map.values()).sort((a, b) =>
      String(a.date).localeCompare(String(b.date)),
    );
  }, [ordersByDay, avgBumpTimeByDay]);

  // Build the month-to-week-to-day hierarchy from backend daily order totals.
  const orderDrill = useMemo(() => {
    const months = new Map();
    const weeks = new Map();
    const days = [];

    for (const row of ordersByDay) {
      const date = String(row?.date || "").slice(0, 10);
      const month = monthKeyFromIso(date);
      const weekStart = toWeekStartYmd(date);
      const orders = Number(row?.total_orders || 0);

      if (!date || !month || !weekStart) continue;

      months.set(month, (months.get(month) || 0) + orders);

      if (!weeks.has(weekStart)) {
        weeks.set(weekStart, { total_orders: 0, months: new Set() });
      }

      weeks.get(weekStart).total_orders += orders;
      weeks.get(weekStart).months.add(month);
      days.push({ date, week_start: weekStart, total_orders: orders });
    }

    return {
      months: Array.from(months, ([period, total_orders]) => ({
        period,
        label: formatMonth(period),
        total_orders,
      })).sort((a, b) => a.period.localeCompare(b.period)),
      weeks,
      days: days.sort((a, b) => a.date.localeCompare(b.date)),
    };
  }, [ordersByDay]);

  const activeMonth = selectedMonth || orderDrill.months.at(-1)?.period || "";

  // Limit the week selector to weeks that overlap the active order month.
  const availableWeeks = useMemo(
    () =>
      Array.from(orderDrill.weeks, ([period, value]) => ({
        period,
        label: formatWeek(period),
        total_orders: value.total_orders,
        months: value.months,
      }))
        .filter((week) => week.months.has(activeMonth))
        .sort((a, b) => a.period.localeCompare(b.period)),
    [orderDrill.weeks, activeMonth]
  );

  const activeWeek = selectedWeek || availableWeeks.at(-1)?.period || "";

  // Reset stale order selections when filters remove their month or week.
  useEffect(() => {
    if (selectedMonth && !orderDrill.months.some((row) => row.period === selectedMonth)) {
      setSelectedMonth("");
      setSelectedWeek("");
    }
  }, [orderDrill.months, selectedMonth]);

  useEffect(() => {
    if (selectedWeek && !availableWeeks.some((row) => row.period === selectedWeek)) {
      setSelectedWeek("");
    }
  }, [availableWeeks, selectedWeek]);

  const orderDrillData = useMemo(() => {
    // Return only the aggregation level currently selected in the Total Orders chart.
    if (DrillLevel === "month") return orderDrill.months;
    if (DrillLevel === "week") return availableWeeks;

    return orderDrill.days
      .filter((row) => row.week_start === activeWeek)
      .map((row) => ({
        ...row,
        period: row.date,
        label: weekdayFromIso(row.date),
      }));
  }, [DrillLevel, orderDrill, availableWeeks, activeWeek]);

  const drillIntoPeriod = (clickedRow) => {
    // Clicking a month opens its weeks, while clicking a week opens its days.
    if (!clickedRow) return;

    if (DrillLevel === "month") {
      setSelectedMonth(clickedRow.period);
      setSelectedWeek("");
      setDrillLevel("week");
      return;
    }

    if (DrillLevel === "week") {
      setSelectedWeek(clickedRow.period);
      setDrillLevel("day");
    }
  };

  const handleOrderBarClick = (_event, itemIdentifier) => {
    // MUI identifies the clicked bar by its dataset index.
    drillIntoPeriod(orderDrillData[itemIdentifier?.dataIndex]);
  };

  const topItemsCalendar = useMemo(() => {
    // Prefer dated item rows, then use order dates only to discover available periods.
    const months = new Set();
    const weeks = new Map();

    for (const row of itemsByDay) {
      const date = String(row?.date || "").slice(0, 10);
      const month = monthKeyFromIso(date);
      const weekStart = toWeekStartYmd(date);

      if (!date || !month || !weekStart) continue;
      months.add(month);
      if (!weeks.has(weekStart)) weeks.set(weekStart, new Set());
      weeks.get(weekStart).add(month);
    }

    if (!months.size) {
      for (const row of orderDrill.months) months.add(row.period);
      for (const [weekStart, value] of orderDrill.weeks) {
        weeks.set(weekStart, new Set(value.months));
      }
    }

    return {
      months: Array.from(months)
        .sort()
        .map((period) => ({ period, label: formatMonth(period) })),
      weeks,
    };
  }, [itemsByDay, orderDrill]);

  const itemsActiveMonth =
    itemsSelectedMonth || topItemsCalendar.months.at(-1)?.period || "";

  const itemsAvailableWeeks = useMemo(
    () =>
      Array.from(topItemsCalendar.weeks, ([period, months]) => ({
        period,
        label: formatWeek(period),
        months,
      }))
        .filter((week) => week.months.has(itemsActiveMonth))
        .sort((a, b) => a.period.localeCompare(b.period)),
    [topItemsCalendar.weeks, itemsActiveMonth]
  );

  const itemsActiveWeek =
    itemsSelectedWeek || itemsAvailableWeeks.at(-1)?.period || "";

  // Reset stale item selections when filters remove their month or week.
  useEffect(() => {
    if (
      itemsSelectedMonth &&
      !topItemsCalendar.months.some((row) => row.period === itemsSelectedMonth)
    ) {
      setItemsSelectedMonth("");
      setItemsSelectedWeek("");
    }
  }, [topItemsCalendar.months, itemsSelectedMonth]);

  useEffect(() => {
    if (
      itemsSelectedWeek &&
      !itemsAvailableWeeks.some((row) => row.period === itemsSelectedWeek)
    ) {
      setItemsSelectedWeek("");
    }
  }, [itemsAvailableWeeks, itemsSelectedWeek]);

  // Query the existing item-summary route once per visible period when daily item rows are unavailable.
  useEffect(() => {
    if (itemsByDay.length || typeof fetchItemsSummaryForRange !== "function") {
      setLegacyItemsByPeriod([]);
      return undefined;
    }

    let cancelled = false;
    let periods = [];

    if (itemsDrillLevel === "month") {
      periods = topItemsCalendar.months.map(({ period }) => ({
        period,
        startDate: `${period}-01`,
        endDate: monthEndYmd(period),
      }));
    } else if (itemsDrillLevel === "week") {
      periods = itemsAvailableWeeks.map(({ period }) => ({
        period,
        startDate: period,
        endDate: addDaysYmd(period, 6),
      }));
    } else if (itemsActiveWeek) {
      periods = Array.from({ length: 7 }, (_, index) => {
        const period = addDaysYmd(itemsActiveWeek, index);
        return { period, startDate: period, endDate: period };
      });
    }

    Promise.all(
      periods.map(async ({ period, startDate, endDate }) => {
        const rows = await fetchItemsSummaryForRange(startDate, endDate);
        return rows.map((row) => ({ ...row, period }));
      })
    )
      .then((groups) => {
        if (!cancelled) setLegacyItemsByPeriod(groups.flat());
      })
      .catch((error) => {
        console.warn("Failed to load Top 5 Items period summaries", error);
        if (!cancelled) setLegacyItemsByPeriod([]);
      });

    return () => {
      cancelled = true;
    };
  }, [
    itemsByDay,
    fetchItemsSummaryForRange,
    itemsDrillLevel,
    topItemsCalendar.months,
    itemsAvailableWeeks,
    itemsActiveWeek,
  ]);

  const topItemsDrill = useMemo(() => {
    // Aggregate item quantities by the Top Items chart's independently selected period.
    const periodItems = new Map();
    const itemTotals = new Map();

    for (const row of itemsByDay) {
      const date = String(row?.date || "").slice(0, 10);
      const month = monthKeyFromIso(date);
      const weekStart = toWeekStartYmd(date);
      const item = String(row?.name || "Unknown Item");
      const quantity = Number(row?.total_quantity || 0);

      if (!date || !weekStart || !Number.isFinite(quantity)) continue;
      if (itemsDrillLevel === "week" && month !== itemsActiveMonth) continue;
      if (itemsDrillLevel === "day" && weekStart !== itemsActiveWeek) continue;

      const period =
        itemsDrillLevel === "month"
          ? month
          : itemsDrillLevel === "week"
            ? weekStart
            : date;

      if (!periodItems.has(period)) periodItems.set(period, new Map());

      const quantities = periodItems.get(period);
      quantities.set(item, (quantities.get(item) || 0) + quantity);
      itemTotals.set(item, (itemTotals.get(item) || 0) + quantity);
    }

    if (!itemsByDay.length) {
      for (const row of legacyItemsByPeriod) {
        const period = String(row?.period || "");
        const item = String(row?.name || "Unknown Item");
        const quantity = Number(row?.total_quantity || 0);

        if (!period || !Number.isFinite(quantity)) continue;
        if (!periodItems.has(period)) periodItems.set(period, new Map());

        const quantities = periodItems.get(period);
        quantities.set(item, (quantities.get(item) || 0) + quantity);
        itemTotals.set(item, (itemTotals.get(item) || 0) + quantity);
      }
    }

    const topItems = Array.from(itemTotals, ([name, total]) => ({ name, total }))
      .sort((a, b) => b.total - a.total || a.name.localeCompare(b.name))
      .slice(0, OVERVIEW_TOP_ITEMS_LIMIT);

    // Keep the chart useful with an all-range summary if period requests return no rows.
    if (!topItems.length) {
      const fallbackItems = itemsData
        .map((row) => ({
          name: String(row?.name || "Unknown Item"),
          total: Number(row?.total_quantity || 0),
        }))
        .filter((row) => Number.isFinite(row.total) && row.total > 0)
        .sort((a, b) => b.total - a.total || a.name.localeCompare(b.name))
        .slice(0, OVERVIEW_TOP_ITEMS_LIMIT);

      const fallbackRow = { period: "Selected range" };
      fallbackItems.forEach((item, index) => {
        fallbackRow[`item_${index}`] = item.total;
      });

      return {
        data: fallbackItems.length ? [fallbackRow] : [],
        series: fallbackItems.map((item, index) => ({
          dataKey: `item_${index}`,
          label: item.name,
          stack: "top-items",
        })),
        isFallback: true,
      };
    }

    const data = Array.from(periodItems, ([period, quantities]) => {
      const result = { period };

      topItems.forEach((item, index) => {
        result[`item_${index}`] = quantities.get(item.name) || 0;
      });

      return result;
    }).sort((a, b) => a.period.localeCompare(b.period));

    return {
      data,
      series: topItems.map((item, index) => ({
        dataKey: `item_${index}`,
        label: item.name,
        stack: "top-items",
      })),
      isFallback: false,
    };
  }, [
    itemsByDay,
    itemsData,
    legacyItemsByPeriod,
    itemsDrillLevel,
    itemsActiveMonth,
    itemsActiveWeek,
  ]);

  const handleTopItemBarClick = (_event, itemIdentifier) => {
    // Drill the Top Items chart without changing the Total Orders chart state.
    if (topItemsDrill.isFallback) return;
    const clickedRow = topItemsDrill.data[itemIdentifier?.dataIndex];

    if (!clickedRow) return;

    if (itemsDrillLevel === "month") {
      setItemsSelectedMonth(clickedRow.period);
      setItemsSelectedWeek("");
      setItemsDrillLevel("week");
      return;
    }

    if (itemsDrillLevel === "week") {
      setItemsSelectedWeek(clickedRow.period);
      setItemsDrillLevel("day");
    }
  };

  // Build one Overview Report row per day using all available ticket data.
  const overviewRows = useMemo(() => buildOverviewReportRows({ ordersByDay, avgBumpTimeByDay, ordersList, ordersBumpMinutes }),
    [ordersByDay, avgBumpTimeByDay, ordersList, ordersBumpMinutes]);


  // Define the Overview Report table columns and value formatting.
  const overviewColumns = [
    {
      field: "date",
      headerName: "Date",
      width: 120,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "orders",
      headerName: "Tickets",
      width: 100,
      type: "number",
      align: "left",
      headerAlign: "left",
    },
    {
      field: "avg_bump",
      headerName: "Avg Bump",
      width: 120,
      type: "number",
      align: "left",
      headerAlign: "left",
      valueFormatter: (value) =>
        value == null
          ? "-"
          : `${Number(value).toFixed(2)} min`,
    },
    {
      field: "median_bump",
      headerName: "Median Bump",
      width: 130,
      type: "number",
      align: "left",
      headerAlign: "left",
      valueFormatter: (value) =>
        value == null
          ? "-"
          : `${Number(value).toFixed(2)} min`,
    },
    {
      field: "slow_tickets",
      headerName: "Slow Tickets",
      width: 120,
      type: "number",
      align: "left",
      headerAlign: "left",
    },
    {
      field: "peak_hour",
      headerName: "Peak Hour",
      width: 120,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "time_performance",
      headerName: "Time Performance",
      flex: 1,
      minWidth: 150,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        const status = params.value;

        const styles = {
          Normal:
            "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
          "Needs Review":
            "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
          Outlier:
            "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
          "Insufficient Data":
            "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300",
        };

        if (!status || status === "-") {
          return (
            <span className="text-gray-400">
              -
            </span>
          );
        }

        return (
          <span
            className={`
              px-2
              py-1
              rounded
              text-xs
              font-medium
              ${styles[status] || ""}
            `}
          >
            {status}
          </span>
        );
      },
    },
    {
      field: "tickets_completed",
      headerName: "Tickets Completed",
      width: 150,
      type: "number",
      align: "left",
      headerAlign: "left",
    },
  ];

  // Calculate the total number of orders in the applied report range.
  const totalOrders = useMemo(
    () =>
      ordersByDay.reduce(
        (sum, row) =>
          sum + Number(row?.total_orders || 0),
        0
      ),
    [ordersByDay]
  );

  // Calculate the overall average from valid individual order bump durations.
  const averageBumpTime = useMemo(() => {
    const values = ordersBumpMinutes
      .map((row) =>
        toReportNumber(row?.bump_minutes)
      )
      .filter((value) => Number.isFinite(value) && value >= 0);

    if (!values.length) {
      return "—";
    }

    const average =
      values.reduce(
        (sum, value) => sum + value,
        0
      ) / values.length;

    return `${average.toFixed(1)} min`;
  }, [ordersBumpMinutes]);

  const averageItemsPerTicket = useMemo(() => {
    // Divide total sold quantity by total orders to calculate average ticket size.
    if (totalOrders <= 0) return "—";

    const backendTotal = Number(itemsData[0]?.total_items_sold);
    const totalItems = Number.isFinite(backendTotal)
      ? backendTotal
      : itemsData.reduce(
          (sum, row) => sum + Number(row?.total_quantity || 0),
          0
        );

    return (totalItems / totalOrders).toFixed(1);
  }, [itemsData, totalOrders]);

  // Sum every weekday bucket before selecting the busiest hour across the full range.
  const peakOrderPeriod = useMemo(() => {
    if (!ordersHeatmap.length) {
      return "—";
    }

    const hourlyTotals = new Map();
    for (const row of ordersHeatmap) {
      const hour = Number(row?.hour_of_day);
      const orders = Number(row?.total_orders || 0);
      if (!Number.isInteger(hour) || hour < 0 || hour > 23) continue;
      hourlyTotals.set(hour, (hourlyTotals.get(hour) || 0) + orders);
    }

    const peak = [...hourlyTotals.entries()].reduce(
      (best, current) => current[1] > best[1] ? current : best,
      [-1, -1],
    );

    return peak[0] >= 0
      ? formatHour12(peak[0])
      : "—";
  }, [ordersHeatmap]);

  return (
    <div className="reports-tab-content grid grid-cols-1 gap-4">
      {/* KPI ROW */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI
          title="Total Orders"
          value={totalOrders.toLocaleString()}
          color="blue"
        />

        <KPI
          title="Avg. Ticket Bump Time"
          value={averageBumpTime}
          color="emerald"
        />

        <KPI
          title="Peak Order Period"
          value={peakOrderPeriod}
          color="yellow"
        />

        <KPI
          title="Avg. Items Per Ticket"
          value={averageItemsPerTicket}
          color="purple"
        />
      </div>

      {/* ORDERS & TICKET PERFORMANCE */}
      <Chart
        type="line"
        title="Orders & Ticket Performance"
        subtitle="Daily order volume and average ticket bump time"
        data={overlayData}
        xAxis={[
          {
            dataKey: "date",
            scaleType: "band",
            valueFormatter: (value) =>
              formatMonthDay(value),
            tickLabelStyle: {
              fontSize: 11,
            },
          },
        ]}
        yAxis={[
          {
            id: "orders",
            label: "Orders",
            min: 0,
          },
          {
            id: "bumpTime",
            label: "Minutes",
            min: 0,
          },
        ]}
        series={[
          {
            dataKey: "total_orders",
            label: "Orders",
            yAxisId: "orders",
          },
          {
            dataKey: "avg_bump_time_minutes",
            label: "Avg. Bump Time (min)",
            yAxisId: "bumpTime",
          },
        ]}
        xAxisTitle="Date"
        isDark={isDark}
        height={400}
      />

      {/* PEAK HOUR HEATMAP */}
      <div className={CARD_CLASS}>
        <h4 className={TITLE_CLASS}>
          Peak Hour Heatmap (Orders)
        </h4>

        {ordersHeatmap.length > 0 ? (
          <OrdersHeatmap
            data={ordersHeatmap}
            isDark={isDark}
          />
        ) : (
          <p className={EMPTY_CLASS}>
            No data available for peak-hour heatmap.
          </p>
        )}
      </div>

      {/* TOTAL ORDERS AND TOP ITEMS, drillable from monthly-weekly-days of that week */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <div className="flex flex-wrap items-center gap-2 mb-2">
            {[
              ["month", "Months"],
              ["week", "Weeks"],
              ["day", "Days"],
            ].map(([level, label]) => (
              <ActionButton
                key={level}
                type="button"
                onClick={() => setDrillLevel(level)}
                variant={DrillLevel === level ? 'contained' : 'outlined'}
                aria-pressed={DrillLevel === level}
              >
                {label}
              </ActionButton>
            ))}

            {DrillLevel !== "month" && (
              <select
                aria-label="Select month"
                value={activeMonth}
                onChange={(event) => {
                  setSelectedMonth(event.target.value);
                  setSelectedWeek("");
                }}
                className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-xs text-gray-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
              >
                {orderDrill.months.map((row) => (
                  <option key={row.period} value={row.period}>
                    {row.label}
                  </option>
                ))}
              </select>
            )}

            {DrillLevel === "day" && (
              <select
                aria-label="Select week"
                value={activeWeek}
                onChange={(event) => setSelectedWeek(event.target.value)}
                className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-xs text-gray-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
              >
                {availableWeeks.map((row) => (
                  <option key={row.period} value={row.period}>
                    {row.label}
                  </option>
                ))}
              </select>
            )}
          </div>

          <Chart
            type="bar"
            title={`Total Orders by ${
              DrillLevel === "month"
                ? "Month"
                : DrillLevel === "week"
                  ? "Week"
                  : "Day"
            }`}
            subtitle={
              DrillLevel === "month"
                ? "Click a month bar to view its weeks"
                : DrillLevel === "week"
                  ? `${formatMonth(activeMonth)} — click a week bar to view its days`
                  : formatWeek(activeWeek)
            }
            data={orderDrillData}
            isDark={isDark}
            height={320}
            xAxis={[
              {
                dataKey: "period",
                scaleType: "band",
                label:
                  DrillLevel === "month"
                    ? "Month"
                    : DrillLevel === "week"
                      ? "Week beginning"
                      : "Day",
                valueFormatter: (value) =>
                  DrillLevel === "month"
                    ? formatMonth(value)
                    : DrillLevel === "week"
                      ? formatMonthDay(value)
                      : `${weekdayFromIso(value)} ${formatMonthDay(value)}`,
                tickLabelStyle: { fontSize: 11 },
              },
            ]}
            yAxis={[{ label: "Orders", min: 0 }]}
            series={[{ dataKey: "total_orders", label: "Orders" }]}
            onItemClick={handleOrderBarClick}
          />
        </div>

        {/*Show top 5 items, stacked bar chart, stacked by the item hover state showing what item*/}
        <div>
          {!topItemsDrill.isFallback && (
            <div className="flex flex-wrap items-center gap-2 mb-2">
              {[
                ["month", "Months"],
                ["week", "Weeks"],
                ["day", "Days"],
              ].map(([level, label]) => (
                <ActionButton
                  key={level}
                  type="button"
                  onClick={() => setItemsDrillLevel(level)}
                  variant={itemsDrillLevel === level ? 'contained' : 'outlined'}
                  aria-pressed={itemsDrillLevel === level}
                >
                  {label}
                </ActionButton>
              ))}

              {itemsDrillLevel !== "month" && (
                <select
                  aria-label="Select Top 5 Items month"
                  value={itemsActiveMonth}
                  onChange={(event) => {
                    setItemsSelectedMonth(event.target.value);
                    setItemsSelectedWeek("");
                  }}
                  className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-xs text-gray-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
                >
                  {topItemsCalendar.months.map((row) => (
                    <option key={row.period} value={row.period}>
                      {row.label}
                    </option>
                  ))}
                </select>
              )}

              {itemsDrillLevel === "day" && (
                <select
                  aria-label="Select Top 5 Items week"
                  value={itemsActiveWeek}
                  onChange={(event) => setItemsSelectedWeek(event.target.value)}
                  className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-xs text-gray-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
                >
                  {itemsAvailableWeeks.map((row) => (
                    <option key={row.period} value={row.period}>
                      {row.label}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          <Chart
          type="bar"
          title={topItemsDrill.isFallback ? "Top 5 Items" : `Top 5 Items by ${
            itemsDrillLevel === "month"
              ? "Month"
              : itemsDrillLevel === "week"
                ? "Week"
                : "Day"
          }`}
          subtitle={
            topItemsDrill.isFallback
              ? "For the currently applied report range; restart/update the backend to enable date drill-down"
              : itemsDrillLevel === "month"
              ? "Click a stacked month bar to view its weeks"
              : itemsDrillLevel === "week"
                ? `${formatMonth(itemsActiveMonth)} — click a stacked week bar to view its days`
                : formatWeek(itemsActiveWeek)
          }
          data={topItemsDrill.data}
          isDark={isDark}
          height={320}
          xAxis={[
            {
              dataKey: "period",
              scaleType: "band",
              label:
                itemsDrillLevel === "month"
                  ? "Month"
                  : itemsDrillLevel === "week"
                    ? "Week beginning"
                    : "Day",
              valueFormatter: (value) =>
                itemsDrillLevel === "month"
                  ? formatMonth(value)
                  : itemsDrillLevel === "week"
                    ? formatMonthDay(value)
                    : `${weekdayFromIso(value)} ${formatMonthDay(value)}`,
              tickLabelStyle: { fontSize: 11 },
            },
          ]}
          yAxis={[{ label: "Quantity sold", min: 0 }]}
          series={topItemsDrill.series}
          onItemClick={handleTopItemBarClick}
          />
        </div>
      </div>

      {/* OVERVIEW REPORT */}
      <Table
        title="Overview Report"
        rows={overviewRows}
        columns={overviewColumns}
        isDark={isDark}
        getRowId={(row) => row.id}
        searchable
        searchPlaceholder="Search by date..."
        exportable
        exportTitle="Overview Report"
        exportFileName="Overview Report"
        exportDateRange={filters}
      />
    </div>
  );
};

export default ChartsSection;
